import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowRight, Check, X, ArrowLeft, Calendar, Plus, Lock, AlertCircle, FileText, Clock, MessageSquare, UserPlus } from 'lucide-react';
import { SessionDetails, Squad, Player, Coach } from '../types';
import { getSessionById, markSessionAttendance, updateSessionAttendanceStatus } from '../services/sessionService';
import { getPlayersBySquad } from '../services/playerService';
import { getCoaches } from '../services/coachService';
import toast from 'react-hot-toast';

interface TrialPlayer {
  name: string;
  comments: string;
}

const AttendanceMarking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionDetails | null>(null);
  const [selectedSquad, setSelectedSquad] = useState<Squad | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [isLoadingCoaches, setIsLoadingCoaches] = useState(false);
  const [coachError, setCoachError] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [trialPlayers, setTrialPlayers] = useState<TrialPlayer[]>([]);
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [newTrialPlayer, setNewTrialPlayer] = useState<TrialPlayer>({ name: '', comments: '' });

  useEffect(() => {
    loadSession();
    loadCoaches();
  }, [id]);

  useEffect(() => {
    if (selectedSquad) {
      loadPlayers(selectedSquad);
    }
  }, [selectedSquad]);

  const loadSession = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const sessionData = await getSessionById(id);

      if (sessionData) {
        // Check if attendance is already marked or session is locked
        if (sessionData.attendanceStatus === 'marked' || sessionData.locked) {
          toast.error('Attendance has already been marked for this session');
          navigate(`/sessions/${id}/summary`);
          return;
        }

        setSession(sessionData);
        if (sessionData.squads.length > 0) {
          setSelectedSquad(sessionData.squads[0]);
        }
        // Load trial players if they exist
        if (sessionData.trialPlayers) {
          setTrialPlayers(sessionData.trialPlayers);
        }
      } else {
        setError('Session not found');
      }
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCoaches = async () => {
    try {
      setIsLoadingCoaches(true);
      setCoachError(null);
      const data = await getCoaches();
      setCoaches(data);
    } catch (err) {
      console.error('Error loading coaches:', err);
      setCoachError('Failed to load coaches. Please try again.');
    } finally {
      setIsLoadingCoaches(false);
    }
  };

  const loadPlayers = async (squad: Squad) => {
    try {
      setIsLoadingPlayers(true);
      const squadPlayers = await getPlayersBySquad(squad);
      setPlayers(squadPlayers);
    } catch (err) {
      console.error('Error loading players:', err);
      toast.error('Failed to load players');
    } finally {
      setIsLoadingPlayers(false);
    }
  };

  const handleAttendanceChange = (playerId: string, present: boolean) => {
    if (!selectedSquad || !session) return;

    setSession(prev => {
      if (!prev) return prev;

      const updatedAttendance = { ...prev.attendance };
      const playerIndex = updatedAttendance[selectedSquad].findIndex(
        a => a.playerId === playerId
      );

      if (playerIndex >= 0) {
        updatedAttendance[selectedSquad][playerIndex].present = present;
      } else {
        updatedAttendance[selectedSquad].push({
          playerId,
          present,
          comments: comments[playerId]
        });
      }

      return { ...prev, attendance: updatedAttendance };
    });
  };

  const handleCommentChange = (playerId: string, comment: string) => {
    setComments(prev => ({ ...prev, [playerId]: comment }));

    if (!selectedSquad || !session) return;

    setSession(prev => {
      if (!prev) return prev;

      const updatedAttendance = { ...prev.attendance };
      const playerIndex = updatedAttendance[selectedSquad].findIndex(
        a => a.playerId === playerId
      );

      if (playerIndex >= 0) {
        updatedAttendance[selectedSquad][playerIndex].comments = comment;
      } else {
        updatedAttendance[selectedSquad].push({
          playerId,
          present: false,
          comments: comment
        });
      }

      return { ...prev, attendance: updatedAttendance };
    });
  };

  const toggleComments = (playerId: string) => {
    setShowComments(prev => ({ ...prev, [playerId]: !prev[playerId] }));
  };

  const handlePrimaryCoachChange = (squadKey: Squad, coachId: string) => {
    if (!session) return;

    setSession(prev => {
      if (!prev) return prev;

      const updatedCoaches = { ...prev.coaches };
      updatedCoaches[squadKey] = {
        ...updatedCoaches[squadKey],
        primary: coachId
      };
      return { ...prev, coaches: updatedCoaches };
    });
  };

  const handleSecondaryCoachChange = (squadKey: Squad, coachId: string) => {
    if (!session) return;

    setSession(prev => {
      if (!prev) return prev;

      const updatedCoaches = { ...prev.coaches };
      updatedCoaches[squadKey] = {
        ...updatedCoaches[squadKey],
        secondary: coachId
      };
      return { ...prev, coaches: updatedCoaches };
    });
  };

  const handleAddTrialPlayer = () => {
    if (!newTrialPlayer.name.trim() || !newTrialPlayer.comments.trim()) {
      toast.error('Please provide both name and comments for the trial player');
      return;
    }

    setTrialPlayers(prev => [...prev, { ...newTrialPlayer }]);
    setNewTrialPlayer({ name: '', comments: '' });
    setShowTrialForm(false);
    toast.success('Trial player added successfully');
  };

  const handleRemoveTrialPlayer = (index: number) => {
    setTrialPlayers(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!session || !id) return;

    try {
      setIsSaving(true);

      // Save attendance for ALL squads in the session (not just the selected one)
      await Promise.all(
        session.squads.map(squad =>
          markSessionAttendance(
            id,
            squad,
            session.attendance[squad],
            session.coaches[squad],
            trialPlayers
          )
        )
      );

      // Mark the session attendance_status as 'marked' so summary can load
      await updateSessionAttendanceStatus(id, 'marked');

      toast.success('Attendance saved successfully!');
      navigate(`/sessions/${id}/summary`, { replace: true });
    } catch (err) {
      console.error('Error saving attendance:', err);
      toast.error('Failed to save attendance. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error || 'Session not found'}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={() => navigate('/sessions')}
              >
                Back to Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If session is locked or attendance is marked, show message and redirect
  if (session.locked || session.attendanceStatus === 'marked') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Lock className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Attendance has already been marked for this session.
              </p>
              <button
                className="mt-2 text-sm font-medium text-yellow-700 hover:text-yellow-600"
                onClick={() => navigate(`/sessions/${id}/summary`)}
              >
                View session summary
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sessionDate = new Date(session.date);
  const formattedDate = sessionDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 sm:pb-8">
      <div className="mb-4 sm:mb-8">
        <button
          type="button"
          className="flex items-center text-navy-800 hover:text-navy-900 mb-2"
          onClick={() => navigate('/sessions')}
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Sessions
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mark Attendance</h1>
        <p className="text-gray-600 text-sm sm:text-base">
          {formattedDate} at {session.time} - {session.location}
        </p>
        <p className="text-gray-600 text-sm sm:text-base">
          {session.type} - {session.groundBooking}
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="bg-gray-50 p-2 sm:p-4 rounded-md mb-6">
            <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-2">
              {session.squads.map((squad) => (
                <button
                  key={squad}
                  type="button"
                  className={`px-4 py-2 rounded-md whitespace-nowrap min-h-[44px] transition-colors flex-shrink-0 ${selectedSquad === squad
                    ? 'bg-navy-800 text-white shadow-sm'
                    : 'bg-white text-navy-800 border border-navy-800 hover:bg-navy-50'
                    }`}
                  onClick={() => setSelectedSquad(squad)}
                >
                  {squad}
                </button>
              ))}
            </div>
          </div>

          {selectedSquad ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="form-group">
                  <label className="form-label">Primary Coach</label>
                  <select
                    className="form-input min-h-[44px]"
                    value={session.coaches[selectedSquad].primary}
                    onChange={(e) => handlePrimaryCoachChange(selectedSquad, e.target.value)}
                  >
                    <option value="">Select a primary coach</option>
                    {coaches.map((coach) => (
                      <option key={coach.id} value={coach.id}>
                        {coach.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Secondary Coach</label>
                  <select
                    className="form-input min-h-[44px]"
                    value={session.coaches[selectedSquad].secondary}
                    onChange={(e) => handleSecondaryCoachChange(selectedSquad, e.target.value)}
                  >
                    <option value="">Select a secondary coach (optional)</option>
                    {coaches
                      .filter(coach => coach.id !== session.coaches[selectedSquad].primary)
                      .map((coach) => (
                        <option key={coach.id} value={coach.id}>
                          {coach.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Attendance for {selectedSquad}</label>
                {isLoadingPlayers ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">Loading players...</p>
                  </div>
                ) : (
                  <div className="mt-2 bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                      {players.map((player) => {
                        const attendance = session.attendance[selectedSquad].find(

                          a => a.playerId === player.id
                        );
                        const isPresent = attendance ? attendance.present : false;
                        const comment = attendance?.comments || comments[player.id] || '';
                        const isCommentVisible = showComments[player.id];

                        return (
                          <li key={player.id} className="px-4 py-4 sm:px-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                              <div className="text-base sm:text-sm font-medium text-gray-900 pr-2">
                                {player.name}
                              </div>
                              <div className="flex flex-wrap sm:flex-nowrap gap-2 w-full sm:w-auto">
                                <button
                                  type="button"
                                  className={`flex-1 sm:flex-none flex items-center justify-center px-3 py-2 sm:py-1 rounded-md min-h-[44px] sm:min-h-0 transition-colors ${isPresent
                                    ? 'bg-green-100 text-green-800 ring-1 ring-green-600'
                                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                                    }`}
                                  onClick={() => handleAttendanceChange(player.id, true)}
                                >
                                  <Check size={18} className="mr-1 sm:mr-1 sm:w-4 sm:h-4" />
                                  <span className="font-medium sm:text-sm">Present</span>
                                </button>
                                <button
                                  type="button"
                                  className={`flex-1 sm:flex-none flex items-center justify-center px-3 py-2 sm:py-1 rounded-md min-h-[44px] sm:min-h-0 transition-colors ${!isPresent
                                    ? 'bg-red-100 text-red-800 ring-1 ring-red-600'
                                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                                    }`}
                                  onClick={() => handleAttendanceChange(player.id, false)}
                                >
                                  <X size={18} className="mr-1 sm:mr-1 sm:w-4 sm:h-4" />
                                  <span className="font-medium sm:text-sm">Absent</span>
                                </button>
                                <button
                                  type="button"
                                  className={`flex items-center justify-center px-3 py-2 sm:py-1 rounded-md min-h-[44px] sm:min-h-0 transition-colors w-full sm:w-auto ${isCommentVisible
                                    ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-600'
                                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                                    }`}
                                  onClick={() => toggleComments(player.id)}
                                >
                                  <MessageSquare size={18} className="mr-1 sm:mr-1 sm:w-4 sm:h-4" />
                                  <span className="font-medium sm:text-sm">Comment</span>
                                </button>
                              </div>
                            </div>
                            {isCommentVisible && (
                              <div className="mt-2">
                                <textarea
                                  className="form-input w-full"
                                  placeholder="Add comments (e.g., trial player details, special notes)"
                                  value={comment}
                                  onChange={(e) => handleCommentChange(player.id, e.target.value)}
                                  rows={2}
                                />
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Trial Players Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Trial Players</h3>
                  <button
                    type="button"
                    className="btn-outline flex items-center min-h-[44px]"
                    onClick={() => setShowTrialForm(true)}
                  >
                    <UserPlus size={20} className="mr-2" />
                    Add Trial Player
                  </button>
                </div>

                {showTrialForm && (
                  <div className="bg-white p-4 rounded-lg mb-4">
                    <div className="space-y-4">
                      <div className="form-group">
                        <label className="form-label">Player Name</label>
                        <input
                          type="text"
                          className="form-input min-h-[44px]"
                          value={newTrialPlayer.name}
                          onChange={(e) => setNewTrialPlayer(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter trial player's name"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Comments</label>
                        <textarea
                          className="form-input"
                          value={newTrialPlayer.comments}
                          onChange={(e) => setNewTrialPlayer(prev => ({ ...prev, comments: e.target.value }))}
                          placeholder="Add details about the trial player"
                          rows={3}
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          className="btn-outline min-h-[44px] flex-1 sm:flex-none"
                          onClick={() => setShowTrialForm(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-primary min-h-[44px] flex-1 sm:flex-none"
                          onClick={handleAddTrialPlayer}
                        >
                          Add Player
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {trialPlayers.length > 0 ? (
                  <ul className="divide-y divide-gray-200 bg-white rounded-lg">
                    {trialPlayers.map((player, index) => (
                      <li key={index} className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{player.name}</h4>
                            <p className="mt-1 text-sm text-gray-600">{player.comments}</p>
                          </div>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-800 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2"
                            onClick={() => handleRemoveTrialPlayer(index)}
                          >
                            <X size={20} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    No trial players added yet
                  </p>
                )}
              </div>

              <div className="fixed bottom-16 sm:bottom-auto sm:relative left-0 right-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t sm:border-0 border-gray-200 z-10 flex justify-end shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:shadow-none sm:mt-6">
                <button
                  type="button"
                  className="btn-primary flex items-center w-full justify-center min-h-[44px]"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <Save size={20} className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save Attendance'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Please select a squad to mark attendance.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceMarking;